/**
 * M74 — production multi-route battlefields (not lab copies).
 * Geometry inspired by M69 labs; ids/names match productionPlans.
 */

import type { MapDefinition, WaveDefinition } from '../model/types'
import {
  airRoute,
  emptyGrid,
  groundRoute,
  horiz,
  joinPaths,
  rowsFromGrid,
  setPath,
  vert
} from './gridPaint'

const PROD_GOLD = 250
const PROD_LIVES = 20

function placeholderWaves(): WaveDefinition[] {
  return [
    {
      id: 'p1',
      completionReward: 40,
      groups: [{ enemy: 'normal', count: 6, interval: 0.9, delay: 0.3 }]
    }
  ]
}

function buildMap(
  id: string,
  name: string,
  width: number,
  height: number,
  lanes: { id: string; cells: { x: number; y: number }[] }[],
  air?: { x: number; y: number }[]
): MapDefinition {
  const grid = emptyGrid(width, height)
  for (const lane of lanes) setPath(grid, lane.cells)
  const primary = lanes[0]!
  const routes = lanes.map((lane) => groundRoute(lane.id, lane.cells))
  if (air) routes.push(airRoute('air-a', air))
  return {
    schemaVersion: 1,
    id,
    name,
    width,
    height,
    rows: rowsFromGrid(grid),
    spawn: { ...primary.cells[0]! },
    exit: { ...primary.cells[primary.cells.length - 1]! },
    path: primary.cells.map((c) => ({ ...c })),
    startingGold: PROD_GOLD,
    startingLives: PROD_LIVES,
    waves: placeholderWaves(),
    routes,
    ...(air ? { airRoute: airRoute('air-a', air) } : {})
  }
}

/** Dual independent — far ground lanes, scarce r4.5 dual anchors. */
export function buildTwinspire(): MapDefinition {
  const a = joinPaths([
    vert(2, 0, 6),
    horiz(6, 2, 6),
    vert(6, 6, 12),
    horiz(12, 6, 2),
    vert(2, 12, 18),
    horiz(18, 2, 5),
    vert(5, 18, 25)
  ])
  const b = joinPaths([
    vert(17, 0, 6),
    horiz(6, 17, 13),
    vert(13, 6, 12),
    horiz(12, 13, 17),
    vert(17, 12, 18),
    horiz(18, 17, 14),
    vert(14, 18, 25)
  ])
  const air = [
    { x: 19, y: 24 },
    { x: 19, y: 18 },
    { x: 10, y: 18 },
    { x: 10, y: 8 },
    { x: 0, y: 8 }
  ]
  return buildMap(
    'twinspire',
    'Twinspire',
    20,
    26,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b }
    ],
    air
  )
}

/** Dual converge — separate spawns, shared final trunk. */
export function buildConfluence(): MapDefinition {
  const shared = joinPaths([vert(10, 12, 25)])
  const a = joinPaths([vert(3, 0, 5), horiz(5, 3, 10), vert(10, 5, 12), shared])
  const b = joinPaths([vert(17, 0, 5), horiz(5, 17, 10), vert(10, 5, 12), shared])
  const air = [
    { x: 21, y: 22 },
    { x: 15, y: 16 },
    { x: 10, y: 10 },
    { x: 4, y: 4 },
    { x: 0, y: 0 }
  ]
  return buildMap(
    'confluence',
    'Confluence',
    22,
    26,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b }
    ],
    air
  )
}

/** Air crosses contested ground band between two ground lanes. */
export function buildSkew(): MapDefinition {
  const a = joinPaths([
    vert(4, 0, 6),
    horiz(6, 4, 9),
    vert(9, 6, 14),
    horiz(14, 9, 5),
    vert(5, 14, 25)
  ])
  const b = joinPaths([
    vert(15, 0, 6),
    horiz(6, 15, 10),
    vert(10, 6, 14),
    horiz(14, 10, 14),
    vert(14, 14, 25)
  ])
  const air = [
    { x: 0, y: 24 },
    { x: 0, y: 16 },
    { x: 9, y: 16 },
    { x: 9, y: 10 },
    { x: 19, y: 10 },
    { x: 19, y: 0 }
  ]
  return buildMap(
    'skew',
    'Skew',
    20,
    26,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b }
    ],
    air
  )
}

/** Shared spawn trunk, two distinct exits. */
export function buildDelta(): MapDefinition {
  const trunk = joinPaths([vert(10, 0, 8)])
  const a = joinPaths([trunk, horiz(8, 10, 3), vert(3, 8, 25)])
  const b = joinPaths([trunk, horiz(8, 10, 17), vert(17, 8, 25)])
  return buildMap(
    'delta',
    'Delta',
    22,
    26,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b }
    ]
  )
}

/** Triple ground seams — pairwise premiums only at ordinary range. */
export function buildTriad(): MapDefinition {
  const a = joinPaths([
    vert(2, 0, 5),
    horiz(5, 2, 6),
    vert(6, 5, 12),
    horiz(12, 6, 3),
    vert(3, 12, 20),
    horiz(20, 3, 6),
    vert(6, 20, 27)
  ])
  const b = joinPaths([
    vert(11, 0, 8),
    horiz(8, 11, 9),
    vert(9, 8, 16),
    horiz(16, 9, 12),
    vert(12, 16, 27)
  ])
  const c = joinPaths([
    vert(19, 0, 5),
    horiz(5, 19, 15),
    vert(15, 5, 14),
    horiz(14, 15, 18),
    vert(18, 14, 22),
    horiz(22, 18, 15),
    vert(15, 22, 27)
  ])
  // AIR crosses all three seams — flyers on 2-18 must never fall back to ground.
  const air = [
    { x: 21, y: 26 },
    { x: 21, y: 18 },
    { x: 11, y: 18 },
    { x: 11, y: 10 },
    { x: 0, y: 10 },
    { x: 0, y: 1 }
  ]
  return buildMap(
    'triad',
    'Triad',
    22,
    28,
    [
      { id: 'ground-a', cells: a },
      { id: 'ground-b', cells: b },
      { id: 'ground-c', cells: c }
    ],
    air
  )
}

export const PRODUCTION_MAP_BUILDERS: Record<string, () => MapDefinition> = {
  twinspire: buildTwinspire,
  confluence: buildConfluence,
  skew: buildSkew,
  delta: buildDelta,
  triad: buildTriad
}

export function getProductionMap(id: string): MapDefinition | undefined {
  const build = PRODUCTION_MAP_BUILDERS[id]
  return build ? build() : undefined
}
